import { customerTypeEnum, opportunityStageEnum, tasks } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { attachmentResponseSchema } from './attachment'

export const selectTaskSchema = createSelectSchema(tasks).omit({
  deduplicationKey: true, // Internal field for duplicate prevention
})

export const upsertTaskSchema = createInsertSchema(tasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdByUserId: true, // Will be set from context
  completedAt: true, // Will be set automatically when completed changes
  deduplicationKey: true, // Internal field, not settable via API
})

export const selectTaskWithUserSchema = selectTaskSchema.extend({
  assignedUser: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .optional(),
  createdBy: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .optional(),
  entity: z
    .object({
      id: z.number(),
      name: z.string(),
      type: z.string(),
    })
    .optional(),
  // The list carries attachments without download URLs; detail signs them.
  attachments: z.array(attachmentResponseSchema).optional(),
})

// Sibling entities of a task's current target: the same contact's open leads
// and customerships. Nullable columns are the per-type detail — a lead has a
// stage and a value, a customership has neither.
export const taskEntitySuggestionSchema = z.object({
  type: z.string(),
  id: z.number(),
  name: z.string(),
  stage: z.enum(opportunityStageEnum.enumValues).nullable(),
  orgType: z.enum(customerTypeEnum.enumValues).nullable(),
  estimatedValue: z.string().nullable(),
  address: z.string().nullable(),
  tags: z.array(z.string()),
})

// Type exports
export type Task = z.infer<typeof selectTaskSchema>
export type TaskEntity = NonNullable<TaskWithUser['entity']>
export type TaskEntitySuggestion = z.infer<typeof taskEntitySuggestionSchema>
export type TaskWithUser = z.infer<typeof selectTaskWithUserSchema>
export type UpsertTask = z.infer<typeof upsertTaskSchema>
